// src/components/AddClient.js
import { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import CollapsibleSection from "./forms/CollapsibleSection";
import FormField from "./forms/FormField";
import {
  UserGroupIcon,
  UserIcon,
  DocumentIcon,
  PlusIcon,
  CheckCircleIcon,
  HashIcon,
  BriefcaseIcon,
} from "./icons/FormIcons";
import { useTheme } from "../ThemeContext";

// Location icon for address fields
const LocationIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

// Phone icon
const PhoneIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

// Email icon
const EmailIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const AddClient = ({ onClose, clientId }) => {
  const { theme } = useTheme();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [client, setClient] = useState({
    clientName: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    stateCounty: "",
    country: "",
    postCode: "",
    telephoneNumber: "",
    email: "",
    pointOfContacts: [""],
    companyReference: "",
    taxRegistrationNumber: "",
    otherInfo: "",
  });

  useEffect(() => {
    if (clientId) {
      fetchClient(clientId);
    }
  }, [clientId]);

  const fetchClient = async (clientId) => {
    const clientDoc = await getDoc(doc(db, "clients", clientId));
    if (clientDoc.exists()) {
      const clientData = clientDoc.data();
      const formattedClient = {
        taxRegistrationNumber: "",
        ...clientData,
        pointOfContacts: Array.isArray(clientData.pointOfContacts)
          ? clientData.pointOfContacts
          : [clientData.pointOfContact || ""],
      };
      setClient(formattedClient);
    } else {
      console.log("No such document!");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClient({ ...client, [name]: value });
  };

  const handlePOCChange = (index, e) => {
    const { value } = e.target;
    const updatedPOCs = client.pointOfContacts.map((poc, i) =>
      i === index ? value : poc
    );
    setClient({ ...client, pointOfContacts: updatedPOCs });
  };

  const addPOCField = () => {
    setClient({ ...client, pointOfContacts: [...client.pointOfContacts, ""] });
  };

  const removePOCField = (index) => {
    const updatedPOCs = client.pointOfContacts.filter((_, i) => i !== index);
    setClient({ ...client, pointOfContacts: updatedPOCs });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;

    try {
      setSubmitDisabled(true);
      await addDoc(collection(db, "clients"), {
        ...client,
        createdAt: new Date(),
      });
      onClose();
    } catch (error) {
      console.error("Error adding client: ", error);
    } finally {
      setSubmitDisabled(false);
    }
  };

  const pocSummary = `${client.pointOfContacts.filter((p) => p.trim()).length} contact${
    client.pointOfContacts.filter((p) => p.trim()).length !== 1 ? "s" : ""
  }`;

  return (
    <div className="flex flex-col h-full overflow-hidden rounded-2xl">
      {/* Fixed Header */}
      <div className={`${theme.formHeaderBg} px-8 py-5 rounded-t-2xl`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <UserGroupIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Add New Client</h1>
              <p className={`text-sm ${theme.formSubText} mt-0.5`}>
                Enter client details, contact information, and address
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
            type="button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-1 bg-slate-50">
        <div className="p-6">
          {/* Main Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Client Information Section */}
            <CollapsibleSection
              title="Client Information"
              icon={<BriefcaseIcon />}
              defaultOpen={true}
            >
              <div className="space-y-4">
                {/* Client Name */}
                <FormField
                  label="Client Name"
                  icon={<UserIcon className="w-4 h-4" />}
                  required
                >
                  <input
                    type="text"
                    name="clientName"
                    value={client.clientName}
                    onChange={handleInputChange}
                    placeholder="Enter client name..."
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200"
                  />
                </FormField>

                {/* Company Reference */}
                <FormField
                  label="Company Reference"
                  icon={<HashIcon className="w-4 h-4" />}
                >
                  <input
                    type="text"
                    name="companyReference"
                    value={client.companyReference}
                    onChange={handleInputChange}
                    placeholder="Enter company reference..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200"
                  />
                </FormField>

                <FormField
                  label="Tax Registration Number (TRN)"
                  icon={<HashIcon className="w-4 h-4" />}
                >
                  <input
                    type="text"
                    name="taxRegistrationNumber"
                    value={client.taxRegistrationNumber}
                    onChange={handleInputChange}
                    placeholder="Enter tax registration number..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200"
                  />
                </FormField>
              </div>
            </CollapsibleSection>

            {/* Address Section */}
            <CollapsibleSection
              title="Address"
              icon={<LocationIcon className="w-5 h-5" />}
              defaultOpen={true}
            >
              <div className="space-y-4">
                {/* Address Line 1 */}
                <FormField
                  label="Address Line 1"
                  icon={<LocationIcon className="w-4 h-4" />}
                  required
                >
                  <input
                    type="text"
                    name="addressLine1"
                    value={client.addressLine1}
                    onChange={handleInputChange}
                    placeholder="Street address..."
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200"
                  />
                </FormField>

                {/* Address Line 2 & 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Address Line 2">
                    <input
                      type="text"
                      name="addressLine2"
                      value={client.addressLine2}
                      onChange={handleInputChange}
                      placeholder="Apt, suite, building..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField label="Address Line 3">
                    <input
                      type="text"
                      name="addressLine3"
                      value={client.addressLine3}
                      onChange={handleInputChange}
                      placeholder="Additional address info..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>

                {/* City & State/County */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="City" required>
                    <input
                      type="text"
                      name="city"
                      value={client.city}
                      onChange={handleInputChange}
                      placeholder="City..."
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField label="State/County">
                    <input
                      type="text"
                      name="stateCounty"
                      value={client.stateCounty}
                      onChange={handleInputChange}
                      placeholder="State or county..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>

                {/* Country & Post Code */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Country" required>
                    <input
                      type="text"
                      name="country"
                      value={client.country}
                      onChange={handleInputChange}
                      placeholder="Country..."
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField label="Post Code">
                    <input
                      type="text"
                      name="postCode"
                      value={client.postCode}
                      onChange={handleInputChange}
                      placeholder="Postal code..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>
              </div>
            </CollapsibleSection>

            {/* Contact Information Section */}
            <CollapsibleSection
              title="Contact Information"
              icon={<PhoneIcon className="w-5 h-5" />}
              defaultOpen={true}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Telephone Number"
                    icon={<PhoneIcon className="w-4 h-4" />}
                  >
                    <input
                      type="tel"
                      name="telephoneNumber"
                      value={client.telephoneNumber}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>

                  <FormField
                    label="Email"
                    icon={<EmailIcon className="w-4 h-4" />}
                  >
                    <input
                      type="email"
                      name="email"
                      value={client.email}
                      onChange={handleInputChange}
                      placeholder="email@example.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                             focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                             hover:border-gray-400 transition-all duration-200"
                    />
                  </FormField>
                </div>
              </div>
            </CollapsibleSection>

            {/* Points of Contact Section */}
            <CollapsibleSection
              title="Points of Contact"
              icon={<UserGroupIcon className="w-5 h-5" />}
              badge={pocSummary}
              defaultOpen={true}
            >
              <div className="space-y-4">
                {client.pointOfContacts.map((poc, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">
                        Contact {index + 1}
                      </span>
                      {client.pointOfContacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePOCField(index)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium
                                   hover:bg-red-50 px-2 py-1 rounded-lg transition-all duration-200"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <FormField
                      label={`Point of Contact ${index + 1}`}
                      icon={<UserIcon className="w-4 h-4" />}
                    >
                      <input
                        type="text"
                        value={poc}
                        onChange={(e) => handlePOCChange(index, e)}
                        placeholder="Enter contact name..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                               focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                               hover:border-gray-400 transition-all duration-200"
                      />
                    </FormField>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addPOCField}
                  className="w-full px-6 py-3 border-2 border-dashed border-gray-300 rounded-xl
                         text-gray-700 font-medium flex items-center justify-center gap-2
                         hover:border-slate-500 hover:text-slate-600 hover:bg-slate-50
                         transition-all duration-200"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add Another Contact
                </button>
              </div>
            </CollapsibleSection>

            {/* Additional Information Section */}
            <CollapsibleSection
              title="Additional Information"
              icon={<DocumentIcon className="w-5 h-5" />}
            >
              <div className="space-y-4">
                <FormField
                  label="Other Information"
                  icon={<DocumentIcon className="w-4 h-4" />}
                >
                  <textarea
                    name="otherInfo"
                    value={client.otherInfo}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Add any additional notes or information about this client..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-slate-500 focus:border-slate-500
                           hover:border-gray-400 transition-all duration-200 resize-none"
                  />
                </FormField>
              </div>
            </CollapsibleSection>

            {/* Submit Button */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="flex items-center gap-2 text-green-600">
                      <CheckCircleIcon className="w-4 h-4" />
                      Ready to save
                    </span>
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={submitDisabled}
                  className={`px-8 py-3 ${theme.formHeaderBg} text-white rounded-xl font-medium
                         hover:opacity-90
                         disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                         active:scale-95 transition-all duration-200 shadow-md hover:shadow-xl
                         flex items-center gap-2`}
                >
                  {submitDisabled ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Save Client
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddClient;
